import type {
    FeatureTreeRollbackActionInput,
    FeatureTreeNodeMeta,
    FeatureTreeProviders,
} from './feature-tree-types';

/**
 * 退回控制棒动作依赖。
 *
 * 文件定位：
 * 1. 当前文件位于 web-client/hooks/feature-tree；
 * 2. 只负责把设计树节点动作转发给 cad-core Viewer 门面；
 * 3. 不直接改写树数据，避免 UI 层绕过 cad-core 的退回时间线状态。
 */
interface FeatureRollbackActionContext {
    /** 读取 Viewer 运行时。 */
    getViewer: () => any | null;
    /** 设计树节点元数据映射。 */
    featureTreeNodeMap: Map<string, FeatureTreeNodeMeta>;
    /** 读取 Editor 历史边界中的退回控制棒动作回调。 */
    getHandleFeatureRollbackAction?: () => FeatureTreeProviders['handleFeatureRollbackAction'] | null;
    /** 判断退回控制棒当前是否应禁用。 */
    isRollbackBarDisabled?: () => boolean;
    /** 读取 NaiveUI 消息实例。 */
    getMessage: () => FeatureTreeProviders['message'] | null;
    /** 刷新设计树投影。 */
    refreshTree: () => Promise<{ objectCount: number; commandCount: number }>;
}

/**
 * 退回控制棒动作集合。
 */
export interface FeatureRollbackActionHandlers {
    /** 将控制棒移动到指定特征之前。 */
    handleFeatureRollbackToFeature: (key: string) => Promise<void>;
    /** 将控制棒前滚到指定特征之后。 */
    handleFeatureRollbackForward: (key: string) => Promise<void>;
    /** 将控制棒移动到指定 cursor。 */
    handleFeatureRollbackBarMoveToIndex: (cursorIndex: number) => Promise<void>;
    /** 将控制棒上移一个特征。 */
    handleFeatureRollbackBarMoveUp: () => Promise<void>;
    /** 将控制棒下移一个特征。 */
    handleFeatureRollbackBarMoveDown: () => Promise<void>;
    /** 将控制棒恢复到上一位置。 */
    handleFeatureRollbackToPrevious: () => Promise<void>;
    /** 将控制棒移动到模型末尾。 */
    handleFeatureRollbackToEnd: () => Promise<void>;
}

/**
 * 从设计树节点 key 解析特征 ID。
 *
 * @param context 动作依赖。
 * @param key 设计树节点 key。
 * @returns 可操作特征 ID；非特征节点返回 null。
 */
function resolveFeatureIdFromTreeKey(context: FeatureRollbackActionContext, key: string): string | null {
    const nodeMeta = context.featureTreeNodeMap.get(key);
    if (!nodeMeta?.objectId || nodeMeta.objectType !== 'feature') {
        return null;
    }
    return nodeMeta.objectId;
}

/**
 * 执行退回控制棒动作。
 *
 * 设计说明：
 * 1. 退回控制棒会改变 Worker 当前激活特征前缀，必须通过 Viewer 门面统一重建；
 * 2. action 层只根据节点元数据解析 featureId / cursorIndex，不直接修改树数据；
 * 3. 重建完成后刷新设计树，让灰显状态与图形区同步。
 *
 * @param context 动作依赖。
 * @param work Viewer 退回控制棒动作。
 * @param failureMessage 失败提示。
 */
async function runFeatureRollbackAction(
    context: FeatureRollbackActionContext,
    input: FeatureTreeRollbackActionInput,
    work: (viewer: NonNullable<ReturnType<FeatureRollbackActionContext['getViewer']>>) => Promise<boolean>,
    failureMessage: string,
): Promise<void> {
    if (context.isRollbackBarDisabled?.() === true) {
        /**
         * 草图绘制过程中退回控制棒处于禁用状态。这里在动作层再次截断，
         * 防止键盘、右键菜单或测试脚本绕过组件视觉禁用后直接触发 Viewer 重放。
         */
        return;
    }

    const viewer = context.getViewer();
    const message = context.getMessage();
    if (!viewer) {
        message?.warning('Viewer 未初始化，无法操作退回控制棒');
        return;
    }

    const rollbackActionProvider = context.getHandleFeatureRollbackAction?.() ?? null;
    const succeeded = rollbackActionProvider
        ? await rollbackActionProvider(input)
        : await work(viewer);
    if (!succeeded) {
        message?.warning(failureMessage);
        return;
    }

    await context.refreshTree();
}

/**
 * 创建退回控制棒动作处理器。
 *
 * @param context 动作依赖。
 * @returns 退回控制棒动作集合。
 */
export function createFeatureRollbackActions(
    context: FeatureRollbackActionContext,
): FeatureRollbackActionHandlers {
    /**
     * 将退回控制棒移动到指定特征之前。
     *
     * @param key 设计树节点 key。
     */
    async function handleFeatureRollbackToFeature(key: string): Promise<void> {
        const featureId = resolveFeatureIdFromTreeKey(context, key);
        if (!featureId) {
            return;
        }
        await runFeatureRollbackAction(
            context,
            {
                action: 'rollback-to-feature',
                featureId,
                treeKey: key,
            },
            (viewer) => viewer.rollbackToFeature?.(featureId) ?? Promise.resolve(false),
            '特征退回失败',
        );
    }

    /**
     * 将退回控制棒前滚到指定特征之后。
     *
     * @param key 设计树节点 key。
     */
    async function handleFeatureRollbackForward(key: string): Promise<void> {
        const featureId = resolveFeatureIdFromTreeKey(context, key);
        if (!featureId) {
            return;
        }
        await runFeatureRollbackAction(
            context,
            {
                action: 'roll-forward',
                featureId,
                treeKey: key,
            },
            (viewer) => viewer.rollFeatureRollbackBarForward?.(featureId) ?? Promise.resolve(false),
            '特征前滚失败',
        );
    }

    /**
     * 将退回控制棒移动到指定 cursor。
     *
     * @param cursorIndex 目标插入索引。
     */
    async function handleFeatureRollbackBarMoveToIndex(cursorIndex: number): Promise<void> {
        await runFeatureRollbackAction(
            context,
            {
                action: 'move-to-index',
                cursorIndex,
            },
            async (viewer) => {
                if (!viewer.moveFeatureRollbackBar) {
                    return false;
                }
                await viewer.moveFeatureRollbackBar({cursorIndex});
                return true;
            },
            '移动退回控制棒失败',
        );
    }

    /**
     * 退回控制棒上移一个特征。
     */
    async function handleFeatureRollbackBarMoveUp(): Promise<void> {
        const state = context.getViewer()?.getFeatureRollbackState?.();
        if (!state) {
            return;
        }
        await handleFeatureRollbackBarMoveToIndex(state.cursorIndex - 1);
    }

    /**
     * 退回控制棒下移一个特征。
     */
    async function handleFeatureRollbackBarMoveDown(): Promise<void> {
        const state = context.getViewer()?.getFeatureRollbackState?.();
        if (!state) {
            return;
        }
        await handleFeatureRollbackBarMoveToIndex(state.cursorIndex + 1);
    }

    /**
     * 退回控制棒回到上一位置。
     */
    async function handleFeatureRollbackToPrevious(): Promise<void> {
        await runFeatureRollbackAction(
            context,
            {
                action: 'roll-to-previous',
            },
            (viewer) => viewer.rollFeatureRollbackBarToPrevious?.() ?? Promise.resolve(false),
            '没有可恢复的上一退回位置',
        );
    }

    /**
     * 退回控制棒移动到模型末尾。
     */
    async function handleFeatureRollbackToEnd(): Promise<void> {
        await runFeatureRollbackAction(
            context,
            {
                action: 'roll-to-end',
            },
            (viewer) => viewer.rollFeatureRollbackBarToEnd?.() ?? Promise.resolve(false),
            '退回到尾失败',
        );
    }

    return {
        handleFeatureRollbackToFeature,
        handleFeatureRollbackForward,
        handleFeatureRollbackBarMoveToIndex,
        handleFeatureRollbackBarMoveUp,
        handleFeatureRollbackBarMoveDown,
        handleFeatureRollbackToPrevious,
        handleFeatureRollbackToEnd,
    };
}
